import { Component, Input } from '@angular/core';
import { Notification } from '@app/core/api/models/notifications.model';

@Component({
    selector: 'notice-banner',
    templateUrl: './notice-banner.component.html',
    styleUrls: ['./notice-banner.component.css'],
    standalone: false
})
export class NoticeBannerComponent {
  private _notices: Notification[] = [];
  private _userId: string | null = null;
  dismissedIds: Set<string> = new Set();
  currentIndex = 0;

  @Input() dismissable: boolean = false;

  @Input()
  set notices(value: Notification[] | null | undefined) {
    this._notices = Array.isArray(value) ? value : [];
    this.rebuildDismissedIds();
    this.ensureValidIndex();
  }
  get notices(): Notification[] {
    return this._notices;
  }

  @Input()
  set userId(value: string | null | undefined) {
    this._userId = value ?? null;
    this.rebuildDismissedIds();
  }

  // Return the current user ID
  get userId(): string | null {
    return this._userId;
  }

  // Generate storage keys scoped to the user
  private storageKeys(id: string): string[] {
    return [`notice-dismissed-${this._userId}-${id}`];
  }

  // Rebuild the set of dismissed IDs from localStorage
  private rebuildDismissedIds(): void {
    if (!this.dismissable || !this._notices?.length) {
      this.dismissedIds.clear();
      return;
    }
    const ids = this._notices
      .map(n => n?.Id)
      .filter((id): id is string => !!id)
      .filter(id => this.storageKeys(id).some(k => localStorage.getItem(k) === '1'));

    this.dismissedIds = new Set(ids);
  }

  // Dismiss a notice by its ID
  dismiss(id?: string): void {
    if (!this.dismissable || !id) return;
    const [userScopedKey] = this.storageKeys(id); // Track dismissal per user
    localStorage.setItem(userScopedKey, '1');
    this.dismissedIds.add(id);
    // After dismissing current, advance to the next visible one
    this.moveNext();
  }

  // Check if a notice is dismissed
  isDismissed(id?: string): boolean {
    return !!this.dismissable && !!id && this.dismissedIds.has(id);
  }

  // Determine if there are any visible notices
  hasVisibleNotices(): boolean {
    if (!this.notices.length) return false;
    return this.notices.some(n => !this.isDismissed(n?.Id));
  }

  // Whether there are more than one visible notices (for showing arrows)
  hasMultipleVisibleNotices(): boolean {
    if (!this.notices?.length) return false;
    if (!this.dismissable) return this.notices.length > 1;
    let visibleCount = 0;
    for (const n of this.notices) {
      if (!this.isDismissed(n?.Id)) visibleCount++;
      if (visibleCount > 1) return true;
    }
    return false;
  }

  // position of current notice among visible notices
  get visiblePosition(): number {
    let pos = 0;
    for (let i = 0; i <= this.currentIndex && i < this.notices.length; i++) {
      if (!this.isDismissed(this.notices[i]?.Id)) pos++;
    }
    return pos;
  }

  // Total count of visible (non-dismissed) notices
  get visibleTotal(): number {
    return this.notices.filter(n => !this.isDismissed(n?.Id)).length;
  }

  // Navigation controls
  movePrev(): void {
    if (!this.notices.length) return;
    let nextIndex = (this.currentIndex - 1 + this.notices.length) % this.notices.length;
    let loops = 0;
    while (this.notices.length && this.isDismissed(this.notices[nextIndex]?.Id) && loops < this.notices.length) {
      nextIndex = (nextIndex - 1 + this.notices.length) % this.notices.length;
      loops++;
    }
    this.currentIndex = nextIndex;
  }

  moveNext(): void {
    if (!this.notices.length) return;
    let nextIndex = (this.currentIndex + 1) % this.notices.length;
    let loops = 0;
    while (this.notices.length && this.isDismissed(this.notices[nextIndex]?.Id) && loops < this.notices.length) {
      nextIndex = (nextIndex + 1) % this.notices.length;
      loops++;
    }
    this.currentIndex = nextIndex;
  }

  private ensureValidIndex(): void {
    if (this.currentIndex >= this.notices.length) {
      this.currentIndex = 0;
    }
    // Ensure current points to a visible notice
    if (this.notices.length) {
      let loops = 0;
      while (this.isDismissed(this.notices[this.currentIndex]?.Id) && loops < this.notices.length) {
        this.currentIndex = (this.currentIndex + 1) % this.notices.length;
        loops++;
      }
    }
  }
}
